import React, { useState, useEffect } from 'react';
import { Save, Loader, Info } from 'lucide-react';
import toast from 'react-hot-toast';
import { backupService, BackupSchedule } from '../../services/backupService';

const BackupScheduleForm: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [schedule, setSchedule] = useState<Partial<BackupSchedule>>({
    frequency: 'daily',
    time: '02:00',
    retention_days: 30,
    enabled: false,
  });
  const [existingSchedule, setExistingSchedule] = useState<BackupSchedule | null>(null);

  useEffect(() => {
    loadSchedule();
  }, []);

  const loadSchedule = async () => {
    setLoading(true);
    try {
      const schedules = await backupService.getSchedules();
      if (schedules.length > 0) {
        setExistingSchedule(schedules[0]);
        setSchedule(schedules[0]);
      }
    } catch (error) {
      console.error('Failed to load schedule:', error);
      toast.error('Failed to load schedule');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field: keyof BackupSchedule, value: string | boolean | number) => {
    setSchedule({ ...schedule, [field]: value });
  };

  const handleSave = async () => {
    setSaving(true);

    try {
      if (existingSchedule?.id) {
        await backupService.updateSchedule(existingSchedule.id, schedule);
      } else {
        await backupService.createSchedule(schedule);
      }

      await loadSchedule();
      toast.success('Schedule saved successfully');
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to save schedule');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <Loader className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h3 className="text-lg font-medium text-gray-900">Backup Schedule Configuration</h3>

      {/* Frequency Selection */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Backup Frequency</label>
        <div className="flex space-x-4">
          <label className="flex items-center">
            <input
              type="radio"
              checked={schedule.frequency === 'daily'}
              onChange={() => handleChange('frequency', 'daily')}
              className="mr-2"
            />
            <span className="text-sm">Daily</span>
          </label>
          <label className="flex items-center">
            <input
              type="radio"
              checked={schedule.frequency === 'weekly'}
              onChange={() => handleChange('frequency', 'weekly')}
              className="mr-2"
            />
            <span className="text-sm">Weekly</span>
          </label>
          <label className="flex items-center">
            <input
              type="radio"
              checked={schedule.frequency === 'monthly'}
              onChange={() => handleChange('frequency', 'monthly')}
              className="mr-2"
            />
            <span className="text-sm">Monthly</span>
          </label>
        </div>
      </div>

      {/* Time Selection */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Backup Time</label>
          <input
            type="time"
            value={schedule.time}
            onChange={(e) => handleChange('time', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <p className="mt-1 text-xs text-gray-500">Time when backup should run (server time)</p>
        </div>

        {schedule.frequency === 'weekly' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Day of Week</label>
            <select
              value={schedule.day_of_week ?? 0}
              onChange={(e) => handleChange('day_of_week', parseInt(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value={0}>Sunday</option>
              <option value={1}>Monday</option>
              <option value={2}>Tuesday</option>
              <option value={3}>Wednesday</option>
              <option value={4}>Thursday</option>
              <option value={5}>Friday</option>
              <option value={6}>Saturday</option>
            </select>
          </div>
        )}

        {schedule.frequency === 'monthly' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Day of Month</label>
            <select
              value={schedule.day_of_month ?? 1}
              onChange={(e) => handleChange('day_of_month', parseInt(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => (
                <option key={day} value={day}>{day}</option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Retention Days */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Retention Period (days)</label>
        <input
          type="number"
          value={schedule.retention_days}
          onChange={(e) => handleChange('retention_days', parseInt(e.target.value))}
          min={1}
          max={365}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <p className="mt-1 text-xs text-gray-500">How long to keep old backups before deletion</p>
      </div>

      {/* Enable Schedule */}
      <div>
        <label className="flex items-center">
          <input
            type="checkbox"
            checked={schedule.enabled}
            onChange={(e) => handleChange('enabled', e.target.checked)}
            className="mr-2"
          />
          <span className="text-sm font-medium">Enable automatic scheduled backup</span>
        </label>
      </div>

      {/* Schedule Summary */}
      {schedule.enabled && (
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-md">
          <div className="flex items-start">
            <Info className="w-5 h-5 text-blue-600 mr-2 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-blue-900">Schedule Summary</p>
              <p className="text-sm text-blue-700 mt-1">
                Backup will run {schedule.frequency} at {schedule.time} and keep backups for {schedule.retention_days} days
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex space-x-3 pt-4">
        <button
          onClick={handleSave}
          disabled={saving}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {saving ? (
            <Loader className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Save className="w-4 h-4 mr-2" />
          )}
          {saving ? 'Saving...' : 'Save Schedule'}
        </button>
      </div>

      {/* Note */}
      <div className="p-4 bg-gray-50 border border-gray-200 rounded-md">
        <p className="text-sm text-gray-600">
          <strong>Note:</strong> The schedule uses Celery Beat to run automated backups.
          Make sure the Celery worker and beat scheduler are running. Backups will only
          execute if a NAS configuration is enabled and mounted.
        </p>
      </div>
    </div>
  );
};

export default BackupScheduleForm;
